'use client'

import { useEffect, useMemo, useState } from 'react'
import { FaCalendarAlt, FaLock, FaTimes } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { FaGem } from 'react-icons/fa6'
import Portal from './Portal'
import { useAuth } from '../contexts/AuthContext'

const featuredModelTitles = ['Cinematic Flame', 'Anime Flame', 'Pony XL 2']

const modelDescriptionKeys = {
  'Cinematic Flame': 'modelDescriptionCinematicFlame',
  'Anime Flame': 'modelDescriptionAnimeFlame',
  'Pony Photo XL 2': 'modelDescriptionPonyPhotoXl2',
  'Pony XL 2': 'modelDescriptionPonyXl2',
  'Pony Photo XL': 'modelDescriptionPonyPhotoXl',
  'Pony XL': 'modelDescriptionPonyXl',
  'Pony Illustration XL': 'modelDescriptionPonyIllustrationXl',
  'Pony Render XL': 'modelDescriptionPonyRenderXl',
  'Pony Anime XL': 'modelDescriptionPonyAnimeXl',
  'Cinematic XL': 'modelDescriptionCinematicXl',
  'Hardcore XL': 'modelDescriptionHardcoreXl',
  'Anime XL': 'modelDescriptionAnimeXl',
  Real: 'modelDescriptionReal',
  Anime: 'modelDescriptionAnime',
  Hyperreal: 'modelDescriptionHyperreal',
  Hyperanime: 'modelDescriptionHyperanime',
  Furtoon: 'modelDescriptionFurtoon',
  Fur: 'modelDescriptionFur',
  'K-Pop': 'modelDescriptionKpop',
}

export const models = [
  {
    title: 'Cinematic Flame',
    plan: 'Pro',
    type: 'XL 2',
    style: 'Photoreal',
    releaseDate: '2026',
    gemsCost: 4,
    description: 'Fully true-to-life photos, natural skin, like a real camera.',
    src: '/assets/models/realismxl2.webp',
    chatEnabled: true,
    // Flame predates video support, so both Flame entries were seeded `false`. Video gained a
    // Flame path in Jul 2026 (the base image is fetched from the image backend instead of
    // generated locally) and this flag is not read anywhere — nothing filters on it the way
    // `chat` filters on chatEnabled — so Flame has been video-selectable, and working, since.
    // Corrected to match reality rather than enforced, because every model should be offered.
    videoEnabled: true,
    nonUserEnabled: true,
    new: true,
  },
  {
    title: 'Anime Flame',
    plan: 'Pro',
    type: 'XL 2',
    style: 'Anime',
    releaseDate: '2026',
    gemsCost: 4,
    description: 'Ultra-detailed anime with exceptional prompt accuracy.',
    src: '/assets/models/animeflame.webp',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
    new: true,
  },
  {
    title: 'Pony Photo XL 2',
    plan: 'Trial',
    type: 'XL+',
    style: 'Photoreal',
    releaseDate: '2025',
    gemsCost: 2,
    description:
      'Moody portrait photography—soft shadows, cinematic depth, editorial feel.',
    src: '/assets/models/photoXL2.webp',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Pony XL 2',
    plan: 'Trial',
    type: 'XL+',
    style: 'Hyperreal',
    releaseDate: '2025',
    gemsCost: 2,
    description:
      'Glossy hyperreal look—perfect skin, sharp features, enhanced realism.',
    src: '/assets/models/ponyxl2.webp',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Pony Photo XL',
    plan: 'Trial',
    type: 'XL+',
    style: 'Legacy',
    releaseDate: '2025',
    gemsCost: 2,
    description:
      'Moody portrait photography—soft shadows, cinematic depth, editorial feel.',
    src: '/assets/models/photoXL.webp',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Pony XL',
    plan: 'Trial',
    type: 'XL+',
    style: 'Legacy',
    releaseDate: '2025',
    gemsCost: 2,
    description:
      'Glossy hyperreal look—perfect skin, sharp features, enhanced realism.',
    src: '/assets/models/ponyxl.webp',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Pony Illustration XL',
    plan: 'Trial',
    type: 'XL+',
    style: 'Illustration',
    releaseDate: '2025',
    gemsCost: 2,
    description:
      'Painterly illustration—soft shading, stylised faces, art-focused.',
    src: '/assets/models/illustrationXL.webp',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Pony Render XL',
    plan: 'Pro',
    type: 'XL+',
    // Back from Legacy 2026-10-01: the only 3D-render style, with no alternative in the main
    // list (it went to Legacy 2026-09-22 on low use).
    style: 'Render',
    releaseDate: '2025',
    gemsCost: 2,
    description:
      '3D render style—smooth surfaces, clean lighting, polished finish.',
    src: '/assets/models/renderXL.webp',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Pony Anime XL',
    plan: 'Trial',
    type: 'XL+',
    style: 'Anime',
    releaseDate: '2025',
    gemsCost: 2,
    description:
      'Detailed anime—sharp eyes, soft gradients, expressive characters.',
    src: '/assets/models/ponyanimexl.webp',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Cinematic XL',
    plan: 'Premium',
    src: '/assets/models/cinematicXL.webp',
    type: 'XL',
    // Legacy since 2026-10-01: low use against its successors (backend docs/operations/model-lineup-plan-2026-10.md).
    style: 'Legacy',
    releaseDate: '2024',
    gemsCost: 2,
    description:
      'Classic cinematic look—soft focus, warm tones, film-like portraits.',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Hardcore XL',
    plan: 'Premium',
    src: '/assets/models/hardcoreXl.webp',
    type: 'XL',
    // Legacy since 2026-10-01: low use against its successors (backend docs/operations/model-lineup-plan-2026-10.md).
    style: 'Legacy',
    releaseDate: '2024',
    gemsCost: 2,
    description: 'Gritty realistic look focused on hardcode scenes.',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Anime XL',
    plan: 'Premium',
    src: '/assets/models/animexl.webp',
    type: 'XL',
    style: 'Legacy',
    releaseDate: '2024',
    gemsCost: 2,
    description:
      'Clean anime style—bright tones, simple shading, polished faces.',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Real',
    src: '/assets/models/real.webp',
    plan: 'Free',
    // Legacy since 2026-10-01: low use against its successors (backend docs/operations/model-lineup-plan-2026-10.md).
    style: 'Legacy',
    releaseDate: '2023',
    gemsCost: 1,
    description:
      'Basic photo look—flat lighting, softer detail, quick results.',
    chatEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Anime',
    plan: 'Free',
    src: '/assets/models/anime.webp',
    // Legacy since 2026-10-01: free users' anime is Pony Anime XL (41% of free renders vs 5%
    // here); this is the last old SD model that was still in the main list.
    style: 'Legacy',
    releaseDate: '2023',
    gemsCost: 1,
    description:
      'Simple anime—minimal shading, clean lines, lightweight style.',
    chatEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Hyperreal',
    plan: 'Plus',
    src: '/assets/models/hyperreal.webp',
    style: 'Legacy',
    releaseDate: '2023',
    gemsCost: 1,
    description: 'Stylised realism—glow, contrast, and slightly unreal polish.',
    chatEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Hyperanime',
    plan: 'Plus',
    src: '/assets/models/hyperanime.webp',
    style: 'Legacy',
    releaseDate: '2023',
    gemsCost: 1,
    description: 'Glossy anime—shiny skin, soft glow, exaggerated features.',
    chatEnabled: true,
    nonUserEnabled: true,
  },
  {
    title: 'Furtoon',
    plan: 'Plus',
    // Hidden from every picker since 2026-10-06 (both fur models). The entries stay because the
    // backend still serves them: old images carry these names, and the Telegram bot's catalogue
    // (backend tests/test_telegram_wiring.py) must find each of its models in this list.
    // A remembered choice is moved off them in CreateContext's RETIRED_MODELS.
    hidden: true,
    src: '/assets/models/furtoon.webp',
    style: 'Legacy',
    releaseDate: '2023',
    gemsCost: 1,
    description: 'Cartoon furry—bold shapes, flat colours, playful style.',
    restricted: true,
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: false,
  },
  {
    title: 'Fur',
    plan: 'Plus',
    hidden: true,
    src: '/assets/models/fur.webp',
    style: 'Legacy',
    releaseDate: '2023',
    gemsCost: 1,
    description: 'Soft 3D furry—smooth shading, gentle lighting, plush look.',
    restricted: true,
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: false,
  },
  {
    title: 'K-Pop',
    plan: 'Plus',
    src: '/assets/models/kpop.webp',
    // Legacy since 2026-10-01: low use against its successors (backend docs/operations/model-lineup-plan-2026-10.md).
    style: 'Legacy',
    releaseDate: '2023',
    gemsCost: 1,
    description:
      'Idol aesthetic—flawless skin, soft glow, studio-lit beauty shots.',
    chatEnabled: true,
    videoEnabled: true,
    nonUserEnabled: true,
  },
]

// The order of the main list after the featured models. One list since 2026-10-01: with the line-up
// down to seven models, the style tabs and the version filter mostly showed one card each.
const styleOrder = ['Photoreal', 'Hyperreal', 'Anime', 'Illustration', 'Render']

const getModelVersion = (image) => image.type || 'SD'

const ModelModal = ({
  isOpen,
  closeModal,
  setModel,
  subscription,
  model,
  disabled = [],
  user = false,
  newUser = false,
  chat = false,
  video = false,
}) => {
  const { t } = useTranslation()
  const { isPlusOrHigher, isPremiumOrHigher, isStudioOrHigher } = useAuth()

  const [selectedImage, setSelectedImage] = useState(null)
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [search, setSearch] = useState('')
  const [showLegacy, setShowLegacy] = useState(false)

  useEffect(() => {
    if (isOpen) setShouldRender(true)
  }, [isOpen])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset'

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const canSelectImage = (imagePlan) => {
    switch (imagePlan) {
      case 'Free':
      case 'Trial':
        return true
      case 'Plus':
        return isPlusOrHigher
      case 'Premium':
        return isPremiumOrHigher
      case 'Pro':
        return isStudioOrHigher
      default:
        return false
    }
  }

  const sortedModels = models

  const handleImageClick = (e, image) => {
    e.preventDefault()

    if (!canSelectImage(image.plan)) {
      if (typeof window !== 'undefined') window.location.href = '/upgrade'
      return
    }

    setSelectedImage(image)
    setModel(image.title)
    closeModal()
  }

  // Featured first, then the style order; Legacy models collapse into "Older models", which a
  // search opens by itself when it matches one.
  const visibleModels = useMemo(() => {
    const q = search.trim().toLowerCase()
    return sortedModels
      .filter((image) => !image.hidden)
      .filter((image) => !disabled.includes(image.title))
      .filter((image) => !(image.restricted && (!user || newUser)))
      .filter((image) => !chat || image.chatEnabled)
      .filter((image) => user || image.nonUserEnabled)
      .filter(
        (m) =>
          !q ||
          [m.title, m.style, getModelVersion(m), m.plan, m.description]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(q)),
      )
  }, [sortedModels, disabled, user, newUser, chat, search])

  const mainModels = useMemo(() => {
    const rank = (m) => {
      const featured = featuredModelTitles.indexOf(m.title)
      return featured >= 0
        ? featured
        : featuredModelTitles.length + styleOrder.indexOf(m.style)
    }
    return visibleModels
      .filter((m) => m.style !== 'Legacy')
      .sort((a, b) => rank(a) - rank(b))
  }, [visibleModels])

  const legacyModels = useMemo(
    () => visibleModels.filter((m) => m.style === 'Legacy'),
    [visibleModels],
  )
  const legacyOpen =
    showLegacy || (search.trim() !== '' && legacyModels.length > 0)

  const renderModelCard = (image) => {
    const locked = !canSelectImage(image.plan)
    const version = getModelVersion(image)
    const selected = image.title === model || image === selectedImage

    const planLabel =
      !isPlusOrHigher && image.plan === 'Trial'
        ? '🔥 Pro Trial'
        : image.plan === 'Pro' || image.plan === 'Trial'
          ? '🔥 Pro'
          : image.plan === 'Premium'
            ? '🚀 Premium'
            : image.plan === 'Plus'
              ? '🎉 Plus'
              : image.plan

    return (
      <button
        key={image.title}
        type="button"
        onClick={(e) => !image.maintenance && handleImageClick(e, image)}
        disabled={image.maintenance}
        className={`group text-left relative overflow-visible rounded-md shadow-md min-w-0 transition-all duration-200 ${
          selected
            ? 'ring-2 ring-white'
            : 'ring-1 ring-white/10 hover:ring-white/30'
        } ${
          image.maintenance
            ? 'opacity-60 grayscale cursor-not-allowed'
            : 'cursor-pointer'
        }`}
      >
        <div className="relative w-full aspect-[3/4] overflow-hidden rounded-md">
          <img
            src={image.src}
            alt={image.title}
            className={`object-cover object-top w-full h-full absolute top-0 left-0 rounded-md transition-transform duration-300 ease-in-out transform group-hover:scale-[1.02] ${
              image.maintenance ? 'opacity-30' : ''
            }`}
          />

          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10 pointer-events-none" />

          <div className="absolute top-2 left-2 right-2 z-20 flex items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1.5">
              {image.new && (
                <span className="bg-[var(--main-bg-color)] text-white text-xxs font-bold px-2 py-1 rounded-full shadow-lg">
                  {t('new')}
                </span>
              )}
              {image.earlyAccess && (
                <span className="bg-amber-400 text-black text-xxs font-bold px-2 py-1 rounded-full shadow-lg">
                  {t('eaBadge', 'Early Access')}
                </span>
              )}
            </div>

            <span className="inline-flex items-center justify-center rounded-full bg-black/50 border border-white/10 px-2 py-1 shadow-lg">
              <span className="text-xxs font-black text-gray-300">
                {version}
              </span>
            </span>
          </div>

          <div className="absolute bottom-0 left-0 w-full p-3 z-20">
            <h4 className="text-sm font-semibold text-white line-clamp-2">
              {t(image.title?.toLowerCase())}
            </h4>

            <p className="mt-0.5 text-xs text-gray-200 line-clamp-2">
              {t(
                modelDescriptionKeys[image.title] || 'modelDescriptionGeneral',
                image.description || 'General image generation model.',
              )}
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-1 text-xxs text-white">
              {image.style !== 'Legacy' && (
                <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                  {t(
                    `modelStyle${image.style.replace(/[^A-Za-z0-9]/g, '')}`,
                    image.style,
                  )}
                </span>
              )}
              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                {planLabel}
              </span>

              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10 flex items-center gap-1">
                <FaCalendarAlt />
                {image.releaseDate || '—'}
              </span>

              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10 flex items-center gap-1">
                <FaGem />
                {image.gemsCost ?? '—'}
              </span>
            </div>
          </div>

          {locked && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/55 text-center px-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/60 border border-white/25 shadow-lg">
                <FaLock className="text-xl text-white" />
              </div>

              <p className="mt-3 text-sm font-bold text-white">
                {t('unlockWithPlan', { plan: planLabel })}
              </p>
            </div>
          )}

          {image.maintenance && isPlusOrHigher && (
            <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/20">
              <span className="rounded-full bg-black/60 px-3 py-1.5 text-xs text-white border border-white/10">
                {t('underMaintenance')}
              </span>
            </div>
          )}
        </div>
      </button>
    )
  }

  const gridClass = chat
    ? 'grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 px-1'
    : 'grid grid-cols-1 min-[400px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-1'

  if (!shouldRender) return null

  return (
    <Portal>
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal()
        }}
        className={`fixed z-[110] top-0 left-0 w-full h-full bg-black/60 flex justify-center items-center ${
          isOpen ? 'opacity-100' : 'opacity-0'
        } transition-opacity duration-200 ${
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <div
          className={`bg-[var(--middle-bg-color)] h-[90vh] md:h-[86vh] w-11/12 md:w-4/5 lg:w-2/3 flex flex-col rounded-2xl shadow-2xl p-5 text-gray-100 relative transform transition-all duration-200 ${
            isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        >
          <header className="flex items-start gap-4 pr-12">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-white text-shadow">
                {t('style')}
              </h2>
            </div>

            <button
              onClick={closeModal}
              className="absolute right-4 top-4 text-white hover:text-gray-300"
              style={{ fontSize: '1.5rem' }}
            >
              <FaTimes />
            </button>
          </header>

          <div className="mt-4 flex flex-col gap-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('searchModelsStylesVersions')}
              className="w-full rounded-md bg-black/25 border border-gray-700/80 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-gray-500"
            />
          </div>

          <main className="mt-4 overflow-y-auto overflow-x-visible flex-grow px-1">
            {mainModels.length > 0 && (
              <section className="mb-7">
                <div className={gridClass}>
                  {mainModels.map(renderModelCard)}
                </div>
              </section>
            )}

            {legacyModels.length > 0 && (
              <section className="mb-7">
                <button
                  type="button"
                  onClick={() => setShowLegacy(!legacyOpen)}
                  className="mb-3 flex w-full items-center justify-between rounded-md border border-white/10 px-3 py-2 text-sm font-bold text-gray-100 hover:border-gray-500"
                  aria-expanded={legacyOpen}
                >
                  <span>
                    {t('olderModels', 'Older models')} ({legacyModels.length})
                  </span>
                  <span className="text-gray-400">
                    {legacyOpen ? '−' : '+'}
                  </span>
                </button>
                {legacyOpen && (
                  <div className={gridClass}>
                    {legacyModels.map(renderModelCard)}
                  </div>
                )}
              </section>
            )}
          </main>
        </div>
      </div>
    </Portal>
  )
}

export default ModelModal
